import * as React from "react";
import { Autocomplete, TextField, FormControl, Box, styled, Chip, IconButton, Checkbox, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import { useFilterStyles } from "../../styles/filterStyles";
import VirtualizedListbox from "./VirtualizedListbox";

const StyledChip = styled(Chip)({
  height: '28px',
  fontSize: '12px',
  margin: '2px',
  backgroundColor: '#F3F4F6',
  color: '#374151',
  borderRadius: '6px',
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: '#6B7280',
    '&:hover': {
      color: '#374151',
    },
  },
});

const IncludedChip = styled(Chip)({
  height: '28px',
  fontSize: '12px',
  margin: '2px',
  backgroundColor: '#D1FAE5',
  color: '#065F46',
  borderRadius: '16px',
  border: '1px solid #A7F3D0',
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: '#065F46',
    '&:hover': { color: '#047857' },
  },
});

const ExcludedChip = styled(Chip)({
  height: '28px',
  fontSize: '12px',
  margin: '2px',
  backgroundColor: '#FEF2F2',
  color: '#991B1B',
  borderRadius: '16px',
  border: '1px solid #FECACA',
  '& .MuiChip-label': { textDecoration: 'line-through' },
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: '#991B1B',
    '&:hover': { color: '#7F1D1D' },
  },
});

const StyledCheckbox = styled(Checkbox)({
  padding: '4px',
  color: '#D1D5DB',
  borderRadius: '4px',
  '&.Mui-checked': {
    color: '#3B82F6',
  },
});

interface BrandFilterProps {
  value: string[];
  onChange: (value: string[]) => void;
  options?: string[];
  onActivate?: () => void;
  onDeactivate?: () => void;
  level?: number;
  onClearLevel?: (level: number) => void;
  isLoading?: boolean;
  exclusionMode?: boolean;
  excludedBrands?: string[];
  onExcludedChange?: (brands: string[]) => void;
  allBrands?: string[];
}

export const BrandFilter: React.FC<BrandFilterProps> = ({
  value,
  onChange,
  options,
  onActivate,
  onDeactivate,
  level,
  onClearLevel,
  isLoading,
  exclusionMode,
  excludedBrands,
  onExcludedChange,
  allBrands,
}) => {
  const filterStyles = useFilterStyles();

  const brandOptions = options ?? [];
  const normalizedValue = Array.isArray(value) ? value : [];
  const [searchText, setSearchText] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const autocompleteRef = React.useRef<HTMLDivElement>(null);
  const justOpenedRef = React.useRef(false);

  // In exclusion mode, show cascade-filtered brands first, then other brands
  const dropdownOptions = React.useMemo(() => {
    if (!exclusionMode || !allBrands || allBrands.length === 0) return brandOptions;
    const cascadeSet = new Set(brandOptions);
    const otherBrands = allBrands.filter(b => !cascadeSet.has(b));
    return [...brandOptions, ...otherBrands];
  }, [exclusionMode, allBrands, brandOptions]);

  // Determine which brands are "other" (from allBrands but not in cascade-filtered brandOptions)
  const otherBrandsSet = React.useMemo(() => {
    if (!exclusionMode || !allBrands) return new Set<string>();
    const cascadeSet = new Set(brandOptions);
    return new Set(allBrands.filter(b => !cascadeSet.has(b)));
  }, [exclusionMode, allBrands, brandOptions]);

  // Index of the first "other brand" in the dropdown — used for separator row height
  const separatorIndex = React.useMemo(() => {
    if (!exclusionMode || !allBrands || allBrands.length === 0) return -1;
    const otherCount = allBrands.filter(b => !new Set(brandOptions).has(b)).length;
    return otherCount > 0 ? brandOptions.length : -1;
  }, [exclusionMode, allBrands, brandOptions]);

  const handleChipDelete = (brandToDelete: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onChange(normalizedValue.filter((item) => item !== brandToDelete));
    // Dual-state: if brand is also excluded, clear that too (clean slate)
    if (exclusionMode && excludedBrands?.includes(brandToDelete)) {
      onExcludedChange?.(excludedBrands.filter(b => b !== brandToDelete));
    }
  };

  const handleResetSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onChange([]);
    if (exclusionMode && onExcludedChange) {
      onExcludedChange([]);
    }
  };

  const handleSearchBlur = React.useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (
      relatedTarget?.closest('.MuiAutocomplete-popper') ||
      (relatedTarget && autocompleteRef.current?.contains(relatedTarget))
    ) {
      return;
    }
    setSearchText('');
    setOpen(false);
    onDeactivate?.();
  }, [onDeactivate]);

  const handleClearLevel = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (level && onClearLevel) {
      onClearLevel(level);
    }
  };

  return (
    <>
      {/* Header with Label and Reset Selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <label className={filterStyles.labelText}>Brand{level ? ` (${level})` : ""}</label>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(normalizedValue.length > 0 || (exclusionMode && excludedBrands && excludedBrands.length > 0)) && (
            <span
              onClick={handleResetSelection}
              style={{
                color: '#E91E63',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Reset selection
            </span>
          )}
          {level && onClearLevel && (
            <IconButton
              size="small"
              onClick={handleClearLevel}
              sx={{
                padding: '2px',
                color: '#6B7280',
                '&:hover': {
                  color: '#EF4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Autocomplete Dropdown */}
      <FormControl fullWidth>
        <Autocomplete
          ref={autocompleteRef}
          multiple
          disableCloseOnSelect
          disableClearable
          options={dropdownOptions}
          value={normalizedValue}
          onChange={(_event, newValue) => {
            if (exclusionMode && excludedBrands) {
              // Allow dual state: brands can be both included and excluded
              onChange(newValue);
              // Clean slate: if a brand was unchecked and it was also excluded, clear exclusion
              const removed = normalizedValue.filter(v => !newValue.includes(v));
              if (removed.length > 0) {
                const stillExcluded = excludedBrands.filter(b => !removed.includes(b));
                if (stillExcluded.length !== excludedBrands.length) {
                  onExcludedChange?.(stillExcluded);
                }
              }
            } else {
              onChange(newValue);
            }
          }}
          open={open}
          onOpen={() => {
            justOpenedRef.current = true;
            requestAnimationFrame(() => { justOpenedRef.current = false; });
            setOpen(true);
            onActivate?.();
          }}
          onClose={(_event, reason) => {
            if (reason === 'toggleInput' && justOpenedRef.current) {
              justOpenedRef.current = false;
              return;
            }
            if (reason === 'blur') {
              const relatedTarget = (_event as React.FocusEvent).relatedTarget as HTMLElement | null;
              if (
                relatedTarget?.closest('.MuiAutocomplete-popper') ||
                (relatedTarget && autocompleteRef.current?.contains(relatedTarget))
              ) {
                return;
              }
            }
            setSearchText('');
            setOpen(false);
            onDeactivate?.();
          }}
          filterOptions={(options) => {
            if (!searchText.trim()) return options;
            const q = searchText.toLowerCase();
            return options.filter((opt) => opt.toLowerCase().includes(q));
          }}
          loading={isLoading}
          loadingText={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
              <CircularProgress size={16} />
              <span>Loading options...</span>
            </Box>
          }
          limitTags={0}
          getLimitTagsText={(more) => `${more} items selected`}
          ListboxComponent={VirtualizedListbox}
          popupIcon={<KeyboardArrowDownIcon sx={{ color: '#6B7280', fontSize: '20px' }} />}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={normalizedValue.length === 0 ? "Select brands" : `${normalizedValue.length} items selected`}
            />
          )}
          renderOption={(props, option, { selected }) => {
            const isExcluded = exclusionMode && excludedBrands?.includes(option);
            const isOtherBrand = exclusionMode && otherBrandsSet.has(option);

            if (exclusionMode) {
              const { onClick, ...restProps } = props as any;
              const optionIndex = dropdownOptions.indexOf(option);
              const isFirstOther = isOtherBrand && (optionIndex === 0 || !otherBrandsSet.has(dropdownOptions[optionIndex - 1]));

              // Visual styles on inner Box — VirtualizedListbox overwrites <li> style
              return (
                <li
                  {...restProps}
                  key={option}
                  {...(isFirstOther && { 'data-separator-row': true })}
                  onClick={(e: React.MouseEvent) => {
                    // Above separator: normal include toggle (dual state allowed)
                    // Below separator: block checkbox clicks entirely
                    if (!isOtherBrand && onClick) {
                      onClick(e);
                    }
                  }}
                >
                  {/* Separator zone: line + label (non-interactive, no hover) */}
                  {isFirstOther && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '38px',
                        pointerEvents: 'none',
                        // Separator line
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '6px',
                          left: '8px',
                          right: '8px',
                          height: '1px',
                          backgroundColor: '#E5E7EB',
                        },
                        // "Other brands" label
                        '&::after': {
                          content: '"Other brands"',
                          position: 'absolute',
                          top: '14px',
                          left: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#9CA3AF',
                          letterSpacing: '0.02em',
                        },
                      }}
                    />
                  )}
                  {/* Content row: checkbox + brand name + exclude button */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: isFirstOther ? 'auto' : '100%',
                      height: isFirstOther ? '42px' : '100%',
                      px: 1.5,
                      borderRadius: isFirstOther ? '4px' : 0,
                      // Excluded row: light red/coral tint
                      backgroundColor: isExcluded ? '#FEF2F2' : 'transparent',
                      // Separator row: position content at bottom, handle own hover
                      ...(isFirstOther && {
                        position: 'absolute',
                        bottom: 0,
                        left: '12px',
                        right: '12px',
                        '&:hover': {
                          backgroundColor: isExcluded ? '#FEF2F2' : '#F3F4F6',
                        },
                      }),
                    }}
                  >
                    {/* Left side: checkbox + brand name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <StyledCheckbox
                        checked={selected && !isOtherBrand}
                        disabled={isOtherBrand}
                        sx={{
                          // Below-separator: faded checkbox, not clickable
                          ...(isOtherBrand && {
                            color: '#D1D5DB !important',
                            opacity: 0.35,
                            cursor: 'not-allowed',
                            pointerEvents: 'none',
                          }),
                        }}
                      />
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '14px',
                        // Excluded: strike-through + muted
                        ...(isExcluded ? {
                          textDecoration: 'line-through',
                          color: '#9CA3AF',
                        } : isOtherBrand ? {
                          // Below separator: muted/tertiary text
                          color: '#9CA3AF',
                        } : {
                          // Above separator: normal
                          color: undefined,
                        }),
                        // Prevent long names from breaking layout
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>{option}</span>
                    </Box>

                    {/* Right side: exclusion ⊘ toggle */}
                    <IconButton
                      size="small"
                      aria-label={isExcluded ? `Remove ${option} from exclusions` : `Exclude ${option}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isExcluded) {
                          // Un-exclude: remove from excludedBrands (keeps included state if checked)
                          onExcludedChange?.(excludedBrands!.filter(b => b !== option));
                        } else {
                          // Exclude: add to excludedBrands WITHOUT removing from value (dual state)
                          onExcludedChange?.([...(excludedBrands || []), option]);
                        }
                      }}
                      sx={{
                        padding: '4px',
                        // Excluded: solid red circle with white icon
                        ...(isExcluded ? {
                          backgroundColor: '#EF4444',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#DC2626',
                          },
                          borderRadius: '50%',
                          width: 26,
                          height: 26,
                        } : {
                          // Not excluded — above separator: normal subtle gray
                          // Below separator: slightly more visible so user sees it's clickable
                          color: isOtherBrand ? 'rgba(107, 114, 128, 0.55)' : '#9CA3AF',
                          '&:hover': {
                            backgroundColor: '#F3F4F6',
                            color: '#EF4444',
                          },
                        }),
                      }}
                    >
                      <BlockIcon sx={{ fontSize: isExcluded ? 14 : 18 }} />
                    </IconButton>
                  </Box>
                </li>
              );
            }

            return (
              <li {...props} key={option}>
                <StyledCheckbox checked={selected} />
                <span style={{ marginLeft: '8px', fontSize: '14px' }}>{option}</span>
              </li>
            );
          }}
          noOptionsText={isLoading ? "Loading options..." : "No options available"}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '8px',
              minHeight: '44px',
              fontSize: '14px',
              fontFamily: 'inherit',
              padding: '4px 14px !important',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #E5E7EB',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #068E75',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '2px solid #068E75',
              },
            },
            '& .MuiAutocomplete-input': {
              padding: '6px 0 !important',
            },
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                marginTop: '2px',
              },
            },
            listbox: {
              searchText,
              onSearchChange: setSearchText,
              searchPlaceholder: "Search brands...",
              onSearchBlur: handleSearchBlur,
              separatorIndex,
              sx: {
                padding: 0,
                overflow: 'hidden',
                '&::-webkit-scrollbar': { display: 'none' },
                '& .MuiAutocomplete-option': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#F3F4F6',
                  },
                  // Separator row: suppress <li> hover — inner Box handles it
                  '&[data-separator-row]:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: 'transparent',
                  },
                  '&[aria-selected="true"]:hover': {
                    backgroundColor: '#F3F4F6',
                  },
                },
              },
            } as any,
          }}
        />
      </FormControl>

      {/* Selected Items as Chips below dropdown */}
      {(normalizedValue.length > 0 || (exclusionMode && excludedBrands && excludedBrands.length > 0)) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, marginTop: '8px', maxWidth: '100%' }}>
          {/* Included chips */}
          {normalizedValue.map((brand) => (
            exclusionMode ? (
              <IncludedChip
                key={brand}
                label={brand}
                size="small"
                onDelete={handleChipDelete(brand)}
                deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
              />
            ) : (
              <StyledChip
                key={brand}
                label={brand}
                size="small"
                onDelete={handleChipDelete(brand)}
                deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
              />
            )
          ))}
          {/* Excluded chips — only in exclusion mode */}
          {exclusionMode && excludedBrands?.map((brand) => (
            <ExcludedChip
              key={`excluded-${brand}`}
              label={brand}
              size="small"
              onDelete={() => {
                onExcludedChange?.(excludedBrands.filter(b => b !== brand));
              }}
              deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default BrandFilter;
